/**
 * Guided-tutorial content (issue #427).
 *
 * A tour is an ordered list of steps; a step spotlights one element and
 * completes when the operator clicks it. Shipping a feature that needs
 * explaining means adding a tour here — nothing else. Tours already seen by an
 * operator never run again (see tutorial/progress).
 */

/** Attribute that marks an element as a tutorial target. */
export const TUTORIAL_TARGET_ATTR = 'data-tutorial-id';

/** Spread onto a component to make it addressable by a tutorial step. */
export const tutorialTarget = (id: string) => ({ [TUTORIAL_TARGET_ATTR]: id });

/** CSS selector for a step's target element. */
export const targetSelector = (id: string) =>
  `[${TUTORIAL_TARGET_ATTR}="${id}"]`;

export interface TutorialStep {
  /** Tutorial id of the element to spotlight. */
  target: string;
  /** What the operator should do — one short line. */
  action: string;
  /** What happens once they do it — one short line. */
  outcome: string;
}

export interface Tour {
  /** Stable id — recorded once seen, so never rename a shipped tour. */
  id: string;
  /** Shown as the caption heading. */
  title: string;
  steps: TutorialStep[];
}

/**
 * Tours in the order they were introduced. The oldest one an operator has not
 * seen runs at their next sign-in — so a first-time operator gets the basics,
 * and everyone else only ever gets the tours added since their last visit.
 */
export const TOURS: Tour[] = [
  {
    id: 'planner-basics',
    title: 'Plan your first mission',
    steps: [
      {
        target: 'nav-planner',
        action: 'Open the Planner.',
        outcome: 'The planning map and its route panel take over the screen.',
      },
      {
        target: 'planner-add-waypoints',
        action: 'Click "Add waypoints".',
        outcome: 'The map is now armed — every click drops a numbered waypoint.',
      },
      {
        target: 'planner-map',
        action: 'Click the map where the drone should fly.',
        outcome: 'The route draws itself and measures each leg as you go.',
      },
      {
        target: 'planner-shape-circle',
        action: 'Pick the Circle tool, then drag on the map.',
        outcome: 'You get a coverage area you can move, resize, or sweep with a survey path.',
      },
      {
        target: 'planner-export',
        action: 'Open Export when the route looks right.',
        outcome: 'The mission downloads as a MAVLink file, ready to upload to a drone.',
      },
    ],
  },
  {
    id: 'virtual-drones',
    title: 'New: registering a drone',
    steps: [
      {
        target: 'fleet-register-drone',
        action: 'Open Register a drone and pick Virtual drone.',
        outcome:
          'Spin up a simulated drone and fly a mission without any hardware. ' +
          'The same control adds a real airframe — it asks which first (#389).',
      },
    ],
  },
];
